import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import TicketCard from '../components/TicketCard';
import firebaseService from '../services/firebaseService';
import './Home.css';

const Home = () => {
  const [latestTickets, setLatestTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    const setupSubscription = async () => {
      try {
        // Set up real-time listener for automatic updates
        unsubscribe = await firebaseService.subscribeToMatches((updatedMatches) => {
          if (!isMounted) return;

          console.log('🔔 Home - Real-time update:', updatedMatches?.length || 0, 'matches');

          // Show latest published tickets (pending or live status) from all categories
          // Sort by creation date (most recent first)
          const published = updatedMatches
            .filter(match => match.status === 'pending' || match.status === 'live')
            .sort((a, b) => {
              const dateA = new Date(a.createdAt || 0);
              const dateB = new Date(b.createdAt || 0);
              return dateB - dateA; // Newest first
            })
            .slice(0, 10); // Show latest 10 tickets

          console.log('📊 Home - Latest published tickets:', published.length);
          setLatestTickets(published);
        });
      } catch (error) {
        console.error('❌ Error setting up subscription:', error);
      }
    };

    const fetchTickets = async () => {
      if (!isMounted) return;

      setLoading(true);

      try {
        // Load real matches from Firebase
        const matches = await firebaseService.getAllMatches();
        console.log('📥 Home - Loaded:', matches?.length || 0, 'matches');
        if (!isMounted) return;

        // Show latest published tickets (pending or live status) from all categories
        const published = matches
          .filter(match => match.status === 'pending' || match.status === 'live')
          .sort((a, b) => {
            const dateA = new Date(a.createdAt || 0);
            const dateB = new Date(b.createdAt || 0);
            return dateB - dateA; // Newest first
          })
          .slice(0, 10); // Show latest 10 tickets

        console.log('✅ Home - Setting latest published tickets:', published.length);
        setLatestTickets(published);
      } catch (error) {
        console.error('❌ Error loading tickets:', error);
        if (!isMounted) return;
        setLatestTickets([]);
      }

      if (isMounted) setLoading(false);
    };

    fetchTickets();
    setupSubscription();

    // Cleanup function
    return () => {
      isMounted = false;
      if (unsubscribe && typeof unsubscribe === 'function') {
        try {
          unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing:', error);
        }
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="home">
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
            <p className="loading-text">იტვირთება...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="home"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <motion.h1
            className="page-title"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            უახლესი ტიკეტები
          </motion.h1>
          <motion.p
            className="page-subtitle"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            ბოლო დროს გამოქვეყნებული პროგნოზები
          </motion.p>
        </div>

        {/* Quick Links */}
        <motion.div
          className="quick-links"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Link to="/days-forecast" className="quick-link-card">
            <span className="quick-link-icon">📊</span>
            <span className="quick-link-title">დღის პროგნოზი</span>
          </Link>
          <Link to="/winaswari-prognozi" className="quick-link-card">
            <span className="quick-link-icon">🔮</span>
            <span className="quick-link-title">მაღალრისკიანი</span>
          </Link>
          <Link to="/live" className="quick-link-card">
            <span className="quick-link-icon">🔴</span>
            <span className="quick-link-title">LIVE</span>
          </Link>
          <Link to="/history" className="quick-link-card">
            <span className="quick-link-icon">📜</span>
            <span className="quick-link-title">ისტორია</span>
          </Link>
        </motion.div>

        {/* Latest Tickets */}
        <div className="tickets-section">
          {latestTickets.length > 0 ? (
            <div className="tickets-grid">
              {/* Column Headers */}
              <div className="tc-header">
                <span className="tc-header-label">კლუბები</span>
                <span className="tc-header-label">პროგნოზი</span>
                <span className="tc-header-label">კოეფ. | თარიღი</span>
              </div>
              {latestTickets.map((ticket, index) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  index={index}
                />
              ))}
            </div>
          ) : (
            <div className="empty-container">
              <div className="empty-icon">📭</div>
              <h3 className="empty-title">ტიკეტები ჯერ არ არის</h3>
              <p className="empty-message">
                ახალი პროგნოზები მალე გამოქვეყნდება
              </p>
            </div>
          )}
        </div>

      </div>
    </motion.div>
  );
};

export default Home;
